import { isObject } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import addURLParameters from '../../misc/addURLParameters';
import appPath from '../../misc/appPath';
import { openLink } from '../../misc/openLinks';
import { getVideoThumbnail } from '../../misc/video-thumbnail';
import Modal from '../elements/Modal/Modal';
import SmallSpinner from '../elements/SmallSpinner/SmallSpinner';
import { jsonToCSS } from './jsonToCSS';

// Note: Classes given as prop will only apply styling if that class gets styling from within the iframe.

/*
    To pass styles to the inside of the iframe, use the innerStyle prop in the folowing json format:

    <SandboxedContent innerStyle={{
        body: {
            // your style here
        }
        "other selector": {
            // style props
        }
    }} />

    (i.e., needs another level that the usual style prop for the selector)
 */

const SandboxedContent = (props) => {
    const { className = '', content, noInnerPadding, style = {}, innerStyle, fitHeightToContents } = props,
        src = location.origin + appPath('sandboxed.html') + '#',
        [uid] = useState(uuid),
        [detectedHeight, setDetectedHeight] = useState(),
        [videoURL, setVideoURL] = useState(),
        iframeRef = useRef(),
        [ready, setReady] = useState(false);

    const handleOpenLink = useCallback((url) => {
        if (url) {
            openLink(url, {
                popupCallback: () => {
                    // Bunny.net's player expects autoplay=true instead of 1
                    const autoplay = url.match(/mediadelivery\.net/) ? 'true' : 1;
                    setVideoURL(addURLParameters(url, { autoplay }));
                },
            });
        }
    }, []);

    const sendContent = useCallback(() => {
        try {
            if (iframeRef.current?.contentWindow) {
                iframeRef.current.contentWindow.postMessage(
                    {
                        content: content || '',
                        className: [noInnerPadding ? 'no-inner-padding' : ''].join(' '),
                        style: isObject(innerStyle) ? jsonToCSS(innerStyle) : innerStyle,
                    },
                    '*'
                );
            }
        } catch (err) {
            console.error(err);
            Sentry.captureException(err);
        }
    }, [content, innerStyle, noInnerPadding, fitHeightToContents]);

    useEffect(() => {
        sendContent();
    }, [sendContent, content, ready]);

    const handleReplaceEmbedableWithThumbnail = useCallback(async (link) => {
        try {
            let thumb = await getVideoThumbnail(link);
            iframeRef.current.contentWindow.postMessage(
                {
                    link,
                    thumb: thumb || appPath('img/pixel.png'),
                },
                '*'
            );
        } catch (err) {
            console.warn(err);
        }
    }, []);

    const handleCloseVideo = useCallback(() => {
        setVideoURL(null);
    }, []);

    useEffect(() => {
        function handleMessage(msg) {
            if (msg.data.type && msg.data.uid && msg.data.uid == uid) {
                switch (msg.data.type) {
                    case 'DOCUMENT_HEIGHT':
                        // NOTE: "IF" added to prevent flickering when scroll bar appears/disappears
                        if (msg.data.height >= 0) {
                            if (!detectedHeight || detectedHeight < msg.data.height || Math.abs(detectedHeight - msg.data.height) > 100) {
                                setDetectedHeight(msg.data.height);
                            }
                        }
                        setReady(true);
                        break;
                    case 'OPEN_LINK':
                        handleOpenLink(msg.data.href);
                        break;
                    case 'DOCUMENT_READY':
                        sendContent();
                        break;
                    case 'EMBEDABLE_FOUND':
                        handleReplaceEmbedableWithThumbnail(msg.data.src);
                        break;
                }
            }
        }

        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [handleOpenLink, handleReplaceEmbedableWithThumbnail]);

    if (!content) return null;

    return (
        <>
            {videoURL ? (
                <Modal onClose={handleCloseVideo} fullHeight>
                    <iframe
                        key={videoURL}
                        src={videoURL}
                        style={{ height: '100%' }}
                        frameBorder="0"
                        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen></iframe>
                </Modal>
            ) : null}
            {ready ? null : <SmallSpinner visible={!ready} />}
            <iframe
                className={className}
                src={src + uid}
                sandbox="allow-scripts"
                style={{
                    visibility: ready ? 'visible' : 'hidden',
                    ...style,
                    height: fitHeightToContents ? detectedHeight || 0 : style.height,
                }}
                ref={iframeRef}
            />
        </>
    );
};

export default SandboxedContent;
